import './CustomerFeedbackPopup.css';
import { tVelocityRecord } from './VelocityReport';
import { tTeamMember } from '../data/Team';

interface TeamMemberReportProps {
  velocityHistory: tVelocityRecord[];
  team: tTeamMember[];
  closeTeamMemberReportCallback: () => void;
}

export default function TeamMemberReportPopup(props: TeamMemberReportProps) {
  // Helper function to calculate averages
  const calculateAverages = (totalDevPoints: number, totalQAPoints: number, sprintCount: number) => {
    const avgDevPoints = sprintCount > 0 
      ? (totalDevPoints / sprintCount).toFixed(2) 
      : '0.00';
    const avgQAPoints = sprintCount > 0 
      ? (totalQAPoints / sprintCount).toFixed(2) 
      : '0.00';
    return { avgDevPoints, avgQAPoints };
  };

  // Calculate average dev and QA story points per developer
  const calculateDeveloperAverages = () => {
    const developerStats: { 
      [developerId: number]: { 
        name: string; 
        icon: string; 
        jobTitle: string; 
        totalDevPoints: number; 
        totalQAPoints: number; 
        sprintCount: number 
      } 
    } = {};
    
    // Initialize stats for each team member
    props.team.forEach(member => {
      developerStats[member.id] = {
        name: member.name,
        icon: member.icon,
        jobTitle: member.description,
        totalDevPoints: 0,
        totalQAPoints: 0,
        sprintCount: 0
      };
    });
    
    // Accumulate points from velocity history - also track developers who have worked but aren't in current team
    props.velocityHistory.forEach(record => {
      Object.entries(record.developerVelocity).forEach(([devId, devData]) => {
        const id = parseInt(devId);
        // Initialize stats for developers in velocity history who aren't in the current team
        if (!developerStats[id]) {
          developerStats[id] = {
            name: devData.name,
            icon: devData.icon,
            jobTitle: '', // Job title not available from velocity history
            totalDevPoints: 0,
            totalQAPoints: 0,
            sprintCount: 0
          };
        }
        developerStats[id].totalDevPoints += devData.code;
        developerStats[id].totalQAPoints += devData.qa;
        if (devData.code > 0 || devData.qa > 0) {
          developerStats[id].sprintCount += 1;
        }
      });
    });
    
    return developerStats;
  };
  
  const developerStats = calculateDeveloperAverages();
  
  // Separate current and former employees
  const currentEmployeeIds = new Set(props.team.map(member => member.id));
  const formerEmployees = Object.entries(developerStats)
    .reduce<Array<{ id: number; name: string; icon: string; jobTitle: string; totalDevPoints: number; totalQAPoints: number; sprintCount: number }>>((acc, [devId, stats]) => {
      const id = parseInt(devId);
      if (!currentEmployeeIds.has(id) && stats.sprintCount > 0) {
        acc.push({ id, ...stats });
      }
      return acc;
    }, []);
  
  return (
    <div className="popup-overlay">
      <div className={`customer-feedback-popup-modal`}>
        <h3>⚒️ Team Member Report</h3>
        <table className="feedback-table">
          <thead>
            <tr>
              <th>Developer</th>
              <th>Job Title</th>
              <th>Avg Dev Story Points</th>
              <th>Avg QA Story Points</th>
            </tr>
          </thead>
          <tbody>
            {props.team.map((member) => {
              const stats = developerStats[member.id];
              const { avgDevPoints, avgQAPoints } = calculateAverages(stats.totalDevPoints, stats.totalQAPoints, stats.sprintCount);
              
              return (
                <tr key={member.id}>
                  <td>{member.icon} {member.name}</td>
                  <td>{member.description}</td>
                  <td style={{ textAlign: 'right' }}>{avgDevPoints}</td>
                  <td style={{ textAlign: 'right' }}>{avgQAPoints}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {formerEmployees.length > 0 && (
          <>
            <h3 style={{ marginTop: '20px' }}>Former Employees</h3>
            <table className="feedback-table">
              <thead>
                <tr>
                  <th>Developer</th>
                  <th>Job Title</th>
                  <th>Avg Dev Story Points</th>
                  <th>Avg QA Story Points</th>
                </tr>
              </thead>
              <tbody>
                {formerEmployees.map((former) => {
                  const { avgDevPoints, avgQAPoints } = calculateAverages(former.totalDevPoints, former.totalQAPoints, former.sprintCount);
                  
                  return (
                    <tr key={former.id}>
                      <td>{former.icon} {former.name}</td>
                      <td>{former.jobTitle || 'N/A'}</td>
                      <td style={{ textAlign: 'right' }}>{avgDevPoints}</td>
                      <td style={{ textAlign: 'right' }}>{avgQAPoints}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
        
        <button className="close-button" onClick={props.closeTeamMemberReportCallback}>Close</button>
      </div>
    </div>
  );
}
